<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Accessories</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Accessories List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="accessories"
      :fetch="fetchAccessories"
    >
      <template #table-toolbar>
        <button type="button" class="shared-table-toolbar-button" @click="showModal = true">
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Accessory
        </button>
      </template>

      <template #table-data-actions="trData">
        <td class="px-10 shared-table-cells-cell">
          <div class="flex justify-end">
            <button
              class="mr-2 shared-table-cells-button round edit"
              @click="() => handleSetEditableAccessory(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              class="shared-table-cells-button round delete"
              @click="handleDeleteAccessory(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Modals -->
    <ModalPlus
      :title="editable ? 'Edit Accessory' : 'Add Accessory'"
      :buttonText="editable ? 'Save' : 'Add'"
      :show="showModal"
      :onClose="handleClose"
      :onProceed="handleSubmit(handleProceed)"
      maxWidth="750px"
    >
      <div class="py-8 pb-4">
        <AccessoryForm
          :accessories="accessories.data"
          :errors="errors"
          @set="handleSetField"
          :initialValues="editableAccessory"
        />
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { safeNullDataset, setTranslationColumn } from '@/utils';

import { formMixin } from '@/mixins';

import VSvgIcon from '@/components/base/VSvgIcon';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import Table from '@/components/shared/table/Table';
import AccessoryForm from '@/components/admin/AccessoryForm';

export default {
  name: 'AdminAccessories',
  components: { VSvgIcon, ModalPlus, Table, AccessoryForm },
  mixins: [
    formMixin(
      {
        name: '',
        shop_link: '',
        description: '',
        thumbnail: null,
        is_dashboard: 0,
      },
      ['thumbnail', 'is_dashboard'],
    ),
  ],
  data() {
    return {
      showModal: false,
      editableAccessory: {},
    };
  },
  computed: mapState({
    language: state => state.language,
    accessories: state => ({
      data: safeNullDataset(state)(['admin', 'accessories', 'data']).map(a => ({
        "it's translation": setTranslationColumn(a),
        'netsuite name': a.name,
        ...a,
      })),
      meta: state.admin.accessories.meta,
    }),
    editable() {
      return this.editableAccessory && !!Object.keys(this.editableAccessory).length;
    },
  }),
  methods: {
    async fetchAccessories(params) {
      return await this.$store.dispatch('admin/getAccessories', params);
    },
    handleSetEditableAccessory(accessory) {
      this.editableAccessory = accessory;
      this.showModal = true;
    },
    handleClose() {
      this.handleResetFields();
      this.handleResetErrors();
      this.editableAccessory = {};
      this.showModal = false;
    },
    handleProceed(data) {
      this.editable ? this.handleEditAccessory(data) : this.handleAddAccessory(data);
    },
    setFormData(data) {
      return Object.entries(data).reduce(
        (acc, [key, value]) => (acc.append(key, value), acc),
        new FormData(),
      );
    },
    async handleAddAccessory(data) {
      const { code: lang_code } = this.language;
      const d = { ...data, lang_code };
      const formData = this.setFormData(d);
      await this.$store.dispatch('admin/addAccessory', { data: formData });
      this.handleClose();
    },
    async handleEditAccessory(data) {
      const { code: lang_code } = this.language;
      const d = { ...data, lang_code };
      const formData = this.setFormData(d);
      const id = this.editableAccessory.id;
      await this.$store.dispatch('admin/updateAccessory', { id, data: formData });
      this.handleClose();
    },
    async handleDeleteAccessory(id) {
      await this.$store.dispatch('admin/deleteAccessory', id);
    },
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: "it's translation", isSort: false, classes: { th: 'w-32', td: 'w-32' } },
      { column: 'netsuite name', isSort: true, orderBy: 'name', classes: { th: '', td: '' } },
      { column: 'actions', isSort: false, classes: { th: 'w-24', td: 'w-24' } },
    ];
  },
};
</script>
